import * as Sentry from '@sentry/react';

export const API_BASE = import.meta.env.VITE_API_BASE_URL ?? '';

// Resolves a relative API path to an absolute URL using API_BASE.
// Absolute URLs (e.g. Clerk CDN) are returned as-is.
export function resolveUrl(url: string | null): string | null {
  if (!url) return null;
  if (url.startsWith('/')) return `${API_BASE}${url}`;
  return url;
}

type GetTokenFn = (() => Promise<string | null>) | null;
type ErrorNotifierFn = ((message: string) => void) | null;

let _getToken: GetTokenFn = null;
let _errorNotifier: ErrorNotifierFn = null;

export function setTokenProvider(fn: GetTokenFn) {
  _getToken = fn;
}

export function setErrorNotifier(fn: ErrorNotifierFn) {
  _errorNotifier = fn;
}

async function fetchWithAuth(
  path: string,
  options: RequestInit = {},
  extraHeaders: Record<string, string> = {},
): Promise<Response> {
  const token = _getToken ? await _getToken() : null;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...extraHeaders,
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return fetch(`${API_BASE}${path}`, {
    ...options,
    headers: { ...headers, ...((options.headers as Record<string, string> | undefined) ?? {}) },
  });
}

async function fetchPublic(
  path: string,
  options: RequestInit = {},
  extraHeaders: Record<string, string> = {},
): Promise<Response> {
  return fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...extraHeaders,
      ...((options.headers as Record<string, string> | undefined) ?? {}),
    },
  });
}

// Used for super-admin API calls.
// Uses a relative URL so requests are handled by the Pages Function at app.*/api/super-admin/*,
// which reads the CF_Authorization cookie server-side and forwards it as CF-Access-Token.
// In local dev (Vite proxy), relative URLs go directly to the Worker at :8787.
async function fetchSuperAdmin(path: string, options: RequestInit = {}): Promise<Response> {
  return fetch(path, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...((options.headers as Record<string, string> | undefined) ?? {}),
    },
  });
}

async function handleResponse<T>(
  res: Response,
  suppressStatuses: number[] = [],
  warnStatuses: number[] = [],
): Promise<T> {
  if (!res.ok) {
    const err = (await res.json().catch(() => ({ error: res.statusText }))) as { error?: string };
    const message = err.error ?? res.statusText;
    const error = new Error(message);
    // Additively attach the HTTP status so callers that care (e.g. the player
    // recovery watchdog) can distinguish terminal 4xx from transient 5xx.
    // A pure network failure throws before this and carries no `.status`.
    (error as Error & { status?: number }).status = res.status;
    if (warnStatuses.includes(res.status)) {
      // Expected, user-facing condition (e.g. 429 rate limit) — keep it visible in
      // Sentry for support inquiries, but as a warning-level message so it doesn't
      // trip error-level alerts. captureMessage groups by the message string, so
      // these land in their own issue instead of the noisy stacktrace-fingerprinted
      // group shared with unrelated errors.
      Sentry.captureMessage(message, {
        level: 'warning',
        extra: { status: res.status, url: res.url },
      });
    } else if (!suppressStatuses.includes(res.status)) {
      Sentry.captureException(error, { extra: { status: res.status, url: res.url } });
    }
    _errorNotifier?.(message);
    throw error;
  }
  return res.json() as Promise<T>;
}

// ==================== Auth ====================

export const authApi = {
  async getMe() {
    const res = await fetchWithAuth('/api/auth/me');
    const data = await handleResponse<{
      id: number;
      name: string;
      clerkOrgId: string;
      logoUrl: string | null;
      isActive: boolean;
      createdAt: number;
      storeCount: number;
      hasToken: boolean;
    }>(res);
    return { ...data, logoUrl: resolveUrl(data.logoUrl) };
  },

  async updateAccount(data: { name: string }) {
    const res = await fetchWithAuth('/api/auth/account', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return handleResponse<{ message: string }>(res);
  },

  async uploadLogo(file: File) {
    const token = _getToken ? await _getToken() : null;
    const formData = new FormData();
    formData.append('file', file);
    const res = await fetch(`${API_BASE}/api/auth/logo`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });
    return handleResponse<{ message: string; logoUrl: string }>(res);
  },

  async deleteLogo() {
    const res = await fetchWithAuth('/api/auth/logo', { method: 'DELETE' });
    return handleResponse<{ message: string }>(res);
  },
};

// ==================== Stores ====================

export interface StoreData {
  id: number;
  name: string;
  accessToken: string;
  accessUrl: string;
  isActive: boolean;
  isDemo: boolean;
  createdAt: number;
}

function withFrontendUrl(store: StoreData): StoreData {
  return { ...store, accessUrl: `${window.location.origin}/play/${store.accessToken}` };
}

export const storesApi = {
  async list() {
    const res = await fetchWithAuth('/api/stores');
    return handleResponse<StoreData[]>(res).then((stores) => stores.map(withFrontendUrl));
  },

  async create(name: string) {
    const res = await fetchWithAuth('/api/stores', {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
    return handleResponse<StoreData>(res).then(withFrontendUrl);
  },

  async update(id: number, name: string) {
    const res = await fetchWithAuth(`/api/stores/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ name }),
    });
    return handleResponse<{ id: number; name: string; message: string }>(res);
  },

  async delete(id: number) {
    const res = await fetchWithAuth(`/api/stores/${id}`, { method: 'DELETE' });
    return handleResponse<{ message: string }>(res);
  },

  async regenerateToken(id: number) {
    const res = await fetchWithAuth(`/api/stores/${id}/regenerate-token`, { method: 'PUT' });
    return handleResponse<StoreData>(res).then(withFrontendUrl);
  },
};

// ==================== Player (no Clerk auth) ====================

export const playerApi = {
  async getStores(token: string) {
    const res = await fetchPublic(`/api/play/${token}/stores`);
    const stores =
      await handleResponse<{ id: number; name: string; logoUrl: string | null }[]>(res);
    return stores.map((s) => ({ ...s, logoUrl: resolveUrl(s.logoUrl) }));
  },

  async claimSession(token: string) {
    const res = await fetchPublic(`/api/play/${token}/claim-session`, {
      method: 'POST',
      body: '{}',
    });
    return handleResponse<{ sessionToken: string; heartbeatInterval: number }>(res, [401]);
  },

  async heartbeat(token: string, sessionToken: string): Promise<{ evicted: boolean }> {
    const res = await fetchPublic(`/api/play/${token}/heartbeat`, {
      method: 'POST',
      body: JSON.stringify({ sessionToken }),
    });
    if (res.status === 401 || res.status === 403) return { evicted: true };
    // ignore other non-ok statuses (5xx, network errors handled by caller)
    return { evicted: false };
  },

  async release(token: string, sessionToken: string) {
    const res = await fetchPublic(`/api/play/${token}/release`, {
      method: 'POST',
      body: JSON.stringify({ sessionToken }),
    });
    return handleResponse<{ ok: boolean }>(res, [401]);
  },

  async generate(
    token: string,
    sessionToken: string,
    params: {
      moods: string[];
      genres: string[];
      themes: string[];
      length: number;
      muteStems?: string[];
    },
  ) {
    const res = await fetchPublic(
      `/api/play/${token}/generate`,
      { method: 'POST', body: JSON.stringify(params) },
      { 'X-Session-Token': sessionToken },
    );
    return handleResponse<{ taskId: string; status: string; message: string }>(res, [401], [429]);
  },

  async getTags(
    token: string,
    params: { moods?: string[]; genres?: string[]; themes?: string[] } = {},
  ) {
    const qs = new URLSearchParams();
    (params.moods ?? []).forEach((m) => qs.append('moods', m));
    (params.genres ?? []).forEach((g) => qs.append('genres', g));
    (params.themes ?? []).forEach((t) => qs.append('themes', t));
    const query = qs.toString() ? `?${qs.toString()}` : '';
    const res = await fetchPublic(`/api/play/${token}/tags${query}`);
    return handleResponse<{
      moods: string[];
      genres: string[];
      themes: string[];
      tempos: string[];
    }>(res);
  },

  async getStatus(token: string, taskId: string) {
    const res = await fetchPublic(`/api/play/${token}/status/${taskId}`);
    return handleResponse<{
      status: 'processing' | 'completed' | 'error';
      trackUrl?: string;
      error?: string;
    }>(res);
  },

  async hlsInit(
    token: string,
    sessionToken: string,
    params: { jobId: string; trackAUrl: string; trackBUrl: string },
  ) {
    const res = await fetchPublic(
      `/api/play/${token}/hls/init`,
      { method: 'POST', body: JSON.stringify(params) },
      { 'X-Session-Token': sessionToken },
    );
    return handleResponse<{ jobId: string; status: string }>(res, [401]);
  },

  async hlsExtend(
    token: string,
    sessionToken: string,
    params: { jobId: string; nextTrackUrl: string },
  ) {
    const res = await fetchPublic(
      `/api/play/${token}/hls/extend`,
      { method: 'POST', body: JSON.stringify(params) },
      { 'X-Session-Token': sessionToken },
    );
    return handleResponse<{ jobId: string; status: string }>(res, [401]);
  },

  async hlsStatus(token: string, sessionToken: string, jobId: string) {
    const res = await fetchPublic(`/api/play/${token}/hls/status/${jobId}`, undefined, {
      'X-Session-Token': sessionToken,
    });
    return handleResponse<{
      jobId: string;
      status: 'queued' | 'processing' | 'completed' | 'failed';
      error?: string;
    }>(res, [401]);
  },

  async listPresets(token: string) {
    const res = await fetchPublic(`/api/play/${token}/presets`);
    return handleResponse<
      {
        id: number;
        name: string;
        mood: string | null;
        genres: string[];
        createdAt: number;
        updatedAt: number;
      }[]
    >(res);
  },

  async createPreset(
    token: string,
    params: { name: string; mood?: string | null; genres?: string[] },
  ) {
    const res = await fetchPublic(`/api/play/${token}/presets`, {
      method: 'POST',
      body: JSON.stringify(params),
    });
    return handleResponse<
      | { id: number; name: string; mood: string | null; genres: string[]; createdAt: number }
      | { error: 'duplicate'; existingId: number }
    >(res, [409]);
  },

  async updatePresetName(token: string, presetId: number, name: string) {
    const res = await fetchPublic(`/api/play/${token}/presets/${presetId}`, {
      method: 'PUT',
      body: JSON.stringify({ name }),
    });
    return handleResponse<{ ok: boolean; updatedAt: number } | { error: 'duplicate' }>(res, [409]);
  },

  async overwritePreset(
    token: string,
    presetId: number,
    params: { mood?: string | null; genres?: string[] },
  ) {
    const res = await fetchPublic(`/api/play/${token}/presets/${presetId}/overwrite`, {
      method: 'PUT',
      body: JSON.stringify(params),
    });
    return handleResponse<{
      id: number;
      name: string;
      mood: string | null;
      genres: string[];
      updatedAt: number;
    }>(res);
  },

  async deletePreset(token: string, presetId: number) {
    const res = await fetchPublic(`/api/play/${token}/presets/${presetId}`, {
      method: 'DELETE',
    });
    return handleResponse<{ ok: boolean }>(res);
  },
};

// ==================== Super Admin ====================

export const superAdminApi = {
  async listCompanies() {
    const res = await fetchSuperAdmin('/api/super-admin/companies');
    return handleResponse<
      {
        id: number;
        name: string;
        clerkOrgId: string;
        isActive: boolean;
        createdAt: number;
        storeCount: number;
        hasToken: boolean;
        tokenDescription: string | null;
      }[]
    >(res);
  },

  async updateCompanyToken(id: number, token: string | null, description: string | null) {
    const res = await fetchSuperAdmin(`/api/super-admin/companies/${id}/token`, {
      method: 'PUT',
      body: JSON.stringify({ token, description }),
    });
    return handleResponse<{ message: string }>(res);
  },

  async provision(data: {
    companyName: string;
    userEmail: string;
    soundrawToken: string;
    tokenDescription?: string;
  }) {
    const res = await fetchSuperAdmin('/api/super-admin/provision', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return handleResponse<{
      message: string;
      companyName: string;
      userEmail: string;
      clerkOrgId: string;
    }>(res);
  },

  async deleteCompany(id: number) {
    const res = await fetchSuperAdmin(`/api/super-admin/companies/${id}`, { method: 'DELETE' });
    return handleResponse<{ message: string }>(res);
  },

  async listCompanyStores(companyId: number) {
    const res = await fetchSuperAdmin(`/api/super-admin/companies/${companyId}/stores`);
    return handleResponse<
      { id: number; name: string; accessToken: string; isDemo: boolean; createdAt: number }[]
    >(res);
  },

  async toggleStoreDemo(storeId: number, isDemo: boolean) {
    const res = await fetchSuperAdmin(`/api/super-admin/stores/${storeId}/demo`, {
      method: 'PUT',
      body: JSON.stringify({ isDemo }),
    });
    return handleResponse<{ id: number; isDemo: boolean; message: string }>(res);
  },

  async createStore(companyId: number, name: string) {
    const res = await fetchSuperAdmin(`/api/super-admin/companies/${companyId}/stores`, {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
    return handleResponse<{
      id: number;
      name: string;
      accessToken: string;
      isDemo: boolean;
      createdAt: number;
    }>(res);
  },

  async deleteStore(storeId: number) {
    const res = await fetchSuperAdmin(`/api/super-admin/stores/${storeId}`, { method: 'DELETE' });
    return handleResponse<{ message: string }>(res);
  },
};
